/**
 * PATH: packages/email-hub/src/feature/components/ConnectedMailboxesPanel.tsx
 * FILE_ID: EMAILHUB-FEATURE-COMPONENTS-CONNECTED-MAILBOXES-001
 * PURPOSE: Operator-facing summary of connected mailboxes — provider,
 *          synced email count, last sync time, per-mailbox sync control.
 * WHY: SAM operators need to see which Gmail / Microsoft accounts are
 *      connected and trigger a sync for one mailbox without guessing.
 * FLOW:
 * ┌──────────────┐ ┌─────────────────────┐ ┌────────────────────┐
 * │ /email-      │ → │ Merge facets counts │ → │ Render per-row     │
 * │ accounts +   │   │ + sync/status ages  │   │ sync + visibility  │
 * │ facets       │   │                     │   │ toggles            │
 * └──────────────┘ └─────────────────────┘ └────────────────────┘
 * DEPENDENCIES:
 * - useEmailFacets: mailbox email counts
 * - emailFilterStore: show/hide mailbox in inbox
 * RELATED:
 * - EmailLayout.tsx: mounts this in the sidebar footer
 */

import { useMemo, useState } from 'react'
import {
  AlertTriangle,
  CheckCircle2,
  Eye,
  EyeOff,
  Loader2,
  Lock,
  MailPlus,
  MoreVertical,
  Plus,
  RefreshCw,
  X,
} from 'lucide-react'
import { cn } from '../../_internal/lib/utils'
import { mailboxColor } from '../lib/colorPalettes'
import {
  formatSyncAge,
  providerBadgeClass,
  providerLabel,
} from '../lib/mailboxPresentation'
import { mailboxKey, useEmailFilterStore } from '../stores/emailFilterStore'
import type { EmailAccount } from '../hooks/useEmails'
import type { EmailSyncStatusResponse } from '../hooks/useEmails.types'
import { useEmailFacets } from '../hooks/useEmails'
import { useMailboxContextMenu } from '../hooks/useMailboxContextMenu'
import { MailboxContextMenu } from './MailboxContextMenu'
import { InboxSweepStartDialog } from './InboxSweepStartDialog'

export type ConnectedMailboxesPanelProps = {
  emailAccounts: EmailAccount[]
  syncStatus: EmailSyncStatusResponse | null
  activeEmail?: string | null
  isSyncing: boolean
  onSyncAccount: (accountId: number) => void | Promise<void>
  /** Re-run the OAuth connect flow for the mailbox's provider. When omitted
   *  the right-click "Reconnect" action is hidden. */
  onReconnect?: (acct: EmailAccount) => void
  /** Open the universal Settings modal at the given tab (e.g. 'email-sync',
   *  'email'). When omitted the settings actions are hidden. */
  onOpenSettings?: (tab: string) => void
  /** Connect another Microsoft mailbox. When supplied (with/without
   *  ``onAddGoogle``) a "+ Add mailbox" button renders at the foot of the
   *  panel, so adding a mailbox lives WITH the mailbox list rather than as
   *  a separate nav tab (operator 2026-06-16: "Add Mailbox should just be
   *  a button in mailboxes instead of a tab"). Omit on read-only surfaces. */
  onAddMicrosoft?: () => void
  /** Connect another Google mailbox (see ``onAddMicrosoft``). */
  onAddGoogle?: () => void
  /** Quotech user id of the viewer — the privacy toggle is owner-only. */
  currentUserId?: number | null
  /** Flip a mailbox between private (personal) and shared. When omitted the
   *  right-click "Mark as private" action is hidden. */
  onSetPrivacy?: (accountId: number, makePrivate: boolean) => void | Promise<void>
}

/** Inline "+ Add mailbox" affordance for the foot of the panel. Collapsed
 *  to a single dashed button; expands to a Microsoft / Google provider
 *  picker. Owns its OWN open state so it doesn't couple to the host's
 *  picker — the host only supplies the connect callbacks. */
function AddMailboxRow({
  onMicrosoft,
  onGoogle,
}: {
  onMicrosoft?: () => void
  onGoogle?: () => void
}) {
  const [open, setOpen] = useState(false)
  if (open) {
    return (
      <div className="flex items-center gap-1 pt-0.5" data-testid="add-mailbox-picker">
        {onMicrosoft && (
          <button
            type="button"
            onClick={() => { setOpen(false); onMicrosoft() }}
            className="flex-1 flex items-center justify-center gap-1 rounded-md border border-blue-500/30 bg-blue-500/10 px-2 py-1 text-[10px] font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-500/20 transition-colors"
            title="Sign in with a Microsoft account"
          >
            <MailPlus className="h-3 w-3" /> Microsoft
          </button>
        )}
        {onGoogle && (
          <button
            type="button"
            onClick={() => { setOpen(false); onGoogle() }}
            className="flex-1 flex items-center justify-center gap-1 rounded-md border border-red-500/30 bg-red-500/10 px-2 py-1 text-[10px] font-medium text-red-600 dark:text-red-400 hover:bg-red-500/20 transition-colors"
            title="Sign in with a Google account"
          >
            <MailPlus className="h-3 w-3" /> Google
          </button>
        )}
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="p-1 rounded text-muted-foreground/70 hover:text-foreground hover:bg-accent transition-colors"
          title="Cancel"
          aria-label="Cancel add mailbox"
        >
          <X className="h-3 w-3" />
        </button>
      </div>
    )
  }
  return (
    <button
      type="button"
      onClick={() => setOpen(true)}
      className="mt-0.5 flex w-full items-center justify-center gap-1.5 rounded-md border border-dashed border-border px-2 py-1.5 text-[11px] font-medium text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
      title="Connect another Microsoft or Google mailbox"
    >
      <Plus className="h-3 w-3" /> Add mailbox
    </button>
  )
}

export function ConnectedMailboxesPanel({
  emailAccounts,
  syncStatus,
  activeEmail,
  isSyncing,
  onSyncAccount,
  onReconnect,
  onOpenSettings,
  onAddMicrosoft,
  onAddGoogle,
  currentUserId,
  onSetPrivacy,
}: ConnectedMailboxesPanelProps) {
  const { facets } = useEmailFacets()
  const hidden = useEmailFilterStore((s) => s.hiddenMailboxes)
  const toggle = useEmailFilterStore((s) => s.toggleMailbox)
  const showAll = useEmailFilterStore((s) => s.showAllMailboxes)
  const [sweepTarget, setSweepTarget] = useState<{
    accountId: number
    mailboxLabel: string
  } | null>(null)
  const { syncingId, menu, syncOne, buildItems, openMenu, closeMenu } =
    useMailboxContextMenu({
      onSyncAccount,
      toggleMailbox: toggle,
      onReconnect,
      onOpenSettings,
      currentUserId,
      onSetPrivacy,
      onCleanUpInbox: (accountId, mailboxLabel) => {
        setSweepTarget({ accountId, mailboxLabel })
      },
    })

  const countByAccountId = useMemo(() => {
    const map = new Map<number, number>()
    for (const mb of facets?.mailboxes ?? []) {
      if (mb.account_id != null) {
        map.set(mb.account_id, mb.count ?? 0)
      }
    }
    return map
  }, [facets?.mailboxes])

  const countByUserId = useMemo(() => {
    const map = new Map<number, number>()
    for (const mb of facets?.mailboxes ?? []) {
      if (mb.account_id == null && mb.user_id != null) {
        map.set(mb.user_id, mb.count ?? 0)
      }
    }
    return map
  }, [facets?.mailboxes])

  const statusByAccountId = useMemo(() => {
    const map = new Map<number, NonNullable<EmailSyncStatusResponse['mailboxes']>[number]>()
    for (const row of syncStatus?.mailboxes ?? []) {
      map.set(row.id, row)
    }
    return map
  }, [syncStatus?.mailboxes])

  const hiddenCount = emailAccounts.filter((a) => {
    const key = mailboxKey({ account_id: a.account_id, user_id: a.owner_user_id })
    return key != null && hidden.has(key)
  }).length

  if (emailAccounts.length === 0) return null

  return (
    <div className="flex flex-col gap-1.5 text-[10px] text-muted-foreground">
      <div className="flex items-center justify-between">
        <p className="uppercase tracking-wide opacity-60">
          Connected mailboxes ({emailAccounts.length})
        </p>
        {hiddenCount > 0 && (
          <button
            type="button"
            onClick={showAll}
            className="text-[10px] text-blue-600 dark:text-blue-400 hover:underline"
          >
            Show all
          </button>
        )}
      </div>

      {emailAccounts.map((acct) => {
        const realAccountId = acct.account_id ?? (acct.id > 0 ? acct.id : null)
        const emailCount =
          (realAccountId != null ? countByAccountId.get(realAccountId) : undefined)
          ?? (acct.owner_user_id != null ? countByUserId.get(acct.owner_user_id) : undefined)
          ?? 0
        const syncRow =
          realAccountId != null ? statusByAccountId.get(realAccountId) : undefined
        const lastSyncLabel = formatSyncAge(syncRow?.last_sync_age_seconds)
        const isActive = acct.email_address === activeEmail
        const key = mailboxKey({ account_id: acct.account_id, user_id: acct.owner_user_id })
        const isHidden = key != null && hidden.has(key)
        const c = mailboxColor(acct.email_address)
        const rowSyncing = syncingId === realAccountId || (isSyncing && syncingId == null)
        const canSyncOne = realAccountId != null && realAccountId > 0

        const openRowMenu = (x: number, y: number) =>
          openMenu(x, y, buildItems(acct, { realAccountId, isHidden, key }))

        return (
          <div
            key={acct.id}
            onContextMenu={(e) => {
              e.preventDefault()
              openRowMenu(e.clientX, e.clientY)
            }}
            className={cn(
              'rounded-md border border-border/60 bg-background/40 px-1.5 py-1 space-y-0.5',
              isHidden && 'opacity-60',
            )}
          >
            <div className="flex items-start gap-1 min-w-0">
              <span className={cn('h-1.5 w-1.5 rounded-full shrink-0 mt-1', c.dot)} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1 flex-wrap">
                  <span
                    className={cn(
                      'text-[9px] font-medium uppercase px-1 py-0 rounded',
                      providerBadgeClass(acct.provider),
                    )}
                  >
                    {providerLabel(acct.provider)}
                  </span>
                  {acct.account_type === 'shared' ? (
                    <span className="text-[9px] opacity-60">shared</span>
                  ) : (
                    <span
                      className="text-[9px] opacity-60 inline-flex items-center gap-0.5"
                      title="Private. Only you can see this mailbox"
                    >
                      <Lock className="h-2 w-2" /> private
                    </span>
                  )}
                  {isActive && (
                    <CheckCircle2
                      className="h-2.5 w-2.5 text-emerald-500 shrink-0"
                      aria-label="Primary connected mailbox"
                    />
                  )}
                </div>
                <p
                  className="truncate text-[10px] text-foreground font-medium"
                  title={acct.email_address}
                >
                  {acct.email_address}
                </p>
                <p className="text-[9px] opacity-80">
                  {emailCount.toLocaleString()} email{emailCount === 1 ? '' : 's'} synced
                  {' · '}
                  Last sync {lastSyncLabel}
                </p>
                {syncRow?.last_sync_error && (
                  <p
                    className="text-[9px] text-amber-600 dark:text-amber-400 truncate"
                    title={syncRow.last_sync_error}
                  >
                    {syncRow.last_sync_error}
                  </p>
                )}
                {/* Tokenless mailbox: connected in the list but holds no usable
                    OAuth credentials, so it can never sync and the inbox stays
                    silently empty. Make the dead state explicit + actionable
                    right where the user manages mailboxes.
                    BUG-2026-06-24-EMAIL-TOKENLESS-ZOMBIE. */}
                {syncRow?.needs_reconnect && (
                  <button
                    type="button"
                    onClick={() => onReconnect?.(acct)}
                    disabled={!onReconnect}
                    className="mt-0.5 inline-flex items-center gap-1 text-[9px] font-medium text-red-600 dark:text-red-400 hover:underline disabled:no-underline disabled:cursor-default"
                    title={
                      onReconnect
                        ? `This mailbox isn't connected - reconnect ${acct.email_address} to start syncing`
                        : `This mailbox isn't connected - reconnect it to start syncing`
                    }
                    data-testid={`mailbox-reconnect-required-${acct.id}`}
                  >
                    <AlertTriangle className="h-2.5 w-2.5 shrink-0" /> Reconnect required
                  </button>
                )}
              </div>
              <div className="flex flex-col gap-0.5 shrink-0">
                <button
                  type="button"
                  onClick={(e) => {
                    const r = e.currentTarget.getBoundingClientRect()
                    openRowMenu(r.left, r.bottom + 4)
                  }}
                  className="p-0.5 rounded hover:bg-accent transition-colors"
                  title={`Actions for ${acct.email_address}`}
                  aria-label={`Mailbox actions for ${acct.email_address}`}
                  aria-haspopup="menu"
                >
                  <MoreVertical className="h-3 w-3 opacity-70" />
                </button>
                <button
                  type="button"
                  disabled={!canSyncOne || rowSyncing}
                  onClick={() => {
                    if (canSyncOne && realAccountId != null) void syncOne(realAccountId)
                  }}
                  className={cn(
                    'p-0.5 rounded hover:bg-accent transition-colors disabled:opacity-40',
                    !canSyncOne && 'cursor-not-allowed',
                  )}
                  title={
                    canSyncOne
                      ? `Sync ${acct.email_address} (last 30 days)`
                      : 'Legacy mailbox - use Sync Now for all mailboxes'
                  }
                  aria-label={`Sync ${acct.email_address}`}
                >
                  {rowSyncing ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <RefreshCw className="h-3 w-3" />
                  )}
                </button>
                {key != null && (
                  <button
                    type="button"
                    onClick={() => toggle(key)}
                    className="p-0.5 rounded hover:bg-accent transition-colors"
                    title={
                      isHidden
                        ? `Show emails from ${acct.email_address}`
                        : `Hide emails from ${acct.email_address}`
                    }
                    aria-label={isHidden ? 'Show mailbox in inbox' : 'Hide mailbox from inbox'}
                  >
                    {isHidden ? (
                      <EyeOff className="h-3 w-3 opacity-70" />
                    ) : (
                      <Eye className="h-3 w-3 opacity-70" />
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        )
      })}

      {(onAddMicrosoft || onAddGoogle) && (
        <AddMailboxRow onMicrosoft={onAddMicrosoft} onGoogle={onAddGoogle} />
      )}

      {menu && (
        <MailboxContextMenu
          x={menu.x}
          y={menu.y}
          items={menu.items}
          onClose={closeMenu}
        />
      )}

      {sweepTarget && (
        <InboxSweepStartDialog
          accountId={sweepTarget.accountId}
          mailboxLabel={sweepTarget.mailboxLabel}
          onClose={() => setSweepTarget(null)}
          onExecuted={() => {
            // InboxSweepBanner hydrates from GET /inbox-sweep/ on its own
            // interval; a soft storage event nudges an immediate refresh.
            try {
              window.dispatchEvent(new Event('quotech:inbox-sweep-executed'))
            } catch {
              /* ignore */
            }
          }}
        />
      )}
    </div>
  )
}
